import React from 'react';
import PropTypes from 'prop-types';
import InputControl from '../InputControl/InputControl';
import './TextArea.scss';

const TextArea = ({
  id,
  classes: modifierClasses,
  label,
  name,
  value,
  labelModifier,
  condensed,
  disabled,
  readOnly,
  error,
  microcopy,
  onBlur,
  onChange,
  maxLength,
  inputRef,
  hideLabel,
  attributes,
}) => {
  const additionalProps = {};

  if (error) additionalProps['aria-invalid'] = 'true';
  if (labelModifier === 'required') {
    additionalProps['aria-required'] = 'true';
  }

  const handleOnChange = (e) => {
    onChange(e.target.value);
  };

  const handleOnBlur = (e) => {
    if (onBlur) {
      onBlur(e.target.value);
      return;
    }
    onChange(e.target.value);
  };

  const normalizedPropClasses =
    modifierClasses instanceof Array
      ? modifierClasses
      : [modifierClasses];

  const controllerClasses = [
    'axiom-text-area',
    ...normalizedPropClasses,
  ];

  return (
    <InputControl
      id={id}
      classes={controllerClasses}
      label={label}
      error={error}
      labelModifier={labelModifier}
      microcopy={microcopy}
      hideLabel={hideLabel}
    >
      <div
        className={
          condensed
            ? 'axiom-text-area--input--wrap--condensed input-control--input--wrap--condensed'
            : 'axiom-text-area--input--wrap input-control--input--wrap'
        }
      >
        <textarea
          id={id}
          aria-describedby={
            error || microcopy ? `${id}_messages` : null
          }
          data-testid='textarea'
          name={name}
          value={value}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          className='axiom-text-area--input'
          ref={inputRef}
          {...attributes}
          {...additionalProps}
        />
      </div>
    </InputControl>
  );
};

TextArea.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  labelModifier: PropTypes.oneOf(['required', 'optional']),
  condensed: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  microcopy: PropTypes.string,
  maxLength: PropTypes.number,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  hideLabel: PropTypes.bool,
  attributes: PropTypes.shape({
    autoCapitalize: PropTypes.oneOf([
      'none',
      'sentences',
      'words',
      'characters',
    ]),
    autoComplete: PropTypes.oneOf(['on', 'off']),
    autoCorrect: PropTypes.oneOf(['on', 'off']),
    autoFocus: PropTypes.bool,
    cols: PropTypes.number,
    disabled: PropTypes.bool,
    form: PropTypes.string,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
    name: PropTypes.string,
    placeholder: PropTypes.string, // @todo: disallow (a11y)
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    rows: PropTypes.number,
    spellCheck: PropTypes.oneOf(['true', 'false', 'default']),
    wrap: PropTypes.oneOf(['hard', 'soft', 'off']),
  }),
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

TextArea.defaultProps = {
  onBlur: undefined,
  labelModifier: undefined,
  condensed: false,
  disabled: false,
  readOnly: false,
  error: undefined,
  microcopy: undefined,
  maxLength: undefined,
  inputRef: undefined,
  hideLabel: false,
  attributes: {},
  classes: [],
};

export default TextArea;
